/**
 * Workspace Modules Service
 *
 * Manages workspace-level module toggles — a layer above individual feature flags.
 * Modules are persisted in workspaces.features JSONB so settings survive
 * browser sessions and apply to all workspace members.
 *
 * Canonical dual-gate rule:
 *   accessible = isModuleEnabled(features, key)     // workspace admin decides availability
 *             && isFeatureEnabled(FEATURE_FLAGS.X)  // individual user controls their experience
 *
 * Each module carries a flagKey (the individual feature flag that pairs with it),
 * an overridePolicy (who can set a personal flag override), and a userDefault
 * (whether the flag is on or off for members when the module is first enabled).
 */

import { useState, useEffect, useSyncExternalStore } from 'react';
import { getSupabaseClient } from './supabaseClient';
import { isToolSwitchedOff } from './switchedOffTools';
import { getCurrentWorkspaceId } from './supabaseSync';
import { subscribeWorkspaceContext } from './workspaceContextEvents';
import {
  FLAG_CONFIGS,
  hasFlagOverride,
  isFeatureEnabled,
  type FeatureFlagValue,
} from './featureFlags';

// ─── Types ───────────────────────────────────────────────────────────────────

/**
 * Per-workspace, per-module policy stored in workspaces.module_policies.
 * Workspace admins can override the module definition's static defaults here.
 */
export interface ModulePolicy {
  /** Who can set a personal feature-flag override for this module. */
  override: 'all' | 'admins_only' | 'none';
  /**
   * Default state of the individual flag for all members when the module is enabled.
   * true = opt-out (everyone has it by default); false = opt-in (members must enable).
   */
  userDefault?: boolean;
}

export interface WorkspaceModule {
  key: string;
  name: string;
  description: string;
  /** Implications shown to admin when disabling */
  disableWarning: string;
  /**
   * 'live'         — fully wired; toggle is active.
   * 'needs-wiring' — feature exists but module toggle not yet implemented.
   * 'on-roadmap'   — feature does not yet exist in the app.
   */
  status?: 'live' | 'needs-wiring' | 'on-roadmap';
  /**
   * 'platform' — major capability (default).
   * 'preview'  — newer or experimental UI.
   * 'tools'    — individual prioritization tool.
   * Every group starts ON and a workspace admin can switch any module off
   * (owner ruling 2026-10-14: "default on, not required on").
   */
  group?: 'platform' | 'preview' | 'tools';
  /**
   * For tool modules only — the canonical toolId slug (e.g. 'dot-voting').
   * Used to map between the workspace module key and the tool registry.
   */
  toolId?: string;
  /**
   * For tool modules only — the exact `toolName` strings used by the harmonizer adapters.
   * Multiple values handle cases where one logical tool has variant adapter names.
   * When set, `isToolEnabledByAdapterName` uses this list for lookup.
   */
  adapterNames?: string[];
  /** Whether this module is enabled when no workspace setting has been recorded. */
  defaultEnabled?: boolean;
  /**
   * The FEATURE_FLAGS string value that pairs with this module for individual gating.
   * When set, the dual-gate rule applies: module AND flag must both be true.
   */
  flagKey?: string;
  /**
   * Static default: who is allowed to set a personal feature-flag override.
   * Workspace admins can override this per-workspace via set_workspace_module_policy.
   * 'all'         — any member can opt in or out (default)
   * 'admins_only' — only workspace admins can toggle their individual flag
   * 'none'        — no individual override; module state applies uniformly
   */
  overridePolicy?: 'all' | 'admins_only' | 'none';
  /**
   * Static default: state of the individual flag for all members when module is enabled.
   * true  = opt-out (members have it on by default).
   * false = opt-in (members must enable their own flag).
   * Workspace admins can override per-workspace via set_workspace_module_policy.
   */
  userDefault?: boolean;
  /**
   * When true, omit this module from Settings → Modules. The module remains in
   * the registry (gates still resolve) for cleanup/retirement — see the
   * Deprecation Register entry that owns the hide.
   */
  hideFromModulesSettings?: boolean;
  /**
   * The key of a module this one needs (1036: Delegation on Requests & Agreements).
   * Declared once here and enforced by the one gate: while that module is off —
   * for the workspace (isModuleEnabled) or for this person (resolveModuleGate) —
   * this one is off too. Settings shows it with a line naming what it needs and
   * no switch. Build contract §2.7.
   */
  requires?: string;
  /**
   * 'team' for a module only the Elevation team (Wes and Harry,
   * lib/elevationAccess.ts) can use: its row in Settings → Modules and its
   * flag in Settings → Feature Flags are shown to the team only, and to nobody
   * while it is not yet known who is signed in. Display only: the stored key,
   * flag rows and defaults are untouched, and gates resolve as before.
   */
  elevationLevel?: 'team';
}

// ─── Registry ────────────────────────────────────────────────────────────────

/** The set of modules that workspace admins can toggle. */
export const WORKSPACE_MODULES: WorkspaceModule[] = [
  // ── Live — fully wired ───────────────────────────────────────────────────
  {
    key: 'strategy',
    name: 'Strategy',
    description:
      'Strategies, Objectives, Alignment, and Decisions. When disabled, the Strategy section is removed from navigation and strategy fields become optional on items.',
    disableWarning:
      'Disabling Strategy will hide the Strategy section in the sidebar. Items with existing strategy links will retain those links in the database, but strategy-related fields will be hidden from forms.',
    status: 'live',
    flagKey: 'strategy_nav',
    overridePolicy: 'all',
    userDefault: true,
  },
  {
    key: 'strategy_kernel',
    name: 'Strategy Kernel',
    description:
      'Richard Rumelt\'s Strategy Kernel framework — Diagnosis, Guiding Policy, and Coherent Actions. Provides a structured canvas for defining and stress-testing strategic intent. When disabled, the Strategy Kernel entry is removed from the Strategy section and kernel-related fields are hidden from item and cycle views.',
    disableWarning:
      'Disabling Strategy Kernel will hide the Strategy Kernel section from navigation. Existing kernel records (diagnosis, guiding policy, coherent actions) are retained in the database and will be visible again if the module is re-enabled.',
    status: 'live',
    flagKey: 'strategy_kernel_nav',
    overridePolicy: 'all',
    userDefault: true,
    requires: 'strategy',
  },
  {
    key: 'planning',
    name: 'Planning',
    description:
      'Plans of Record, Plan Cycles, and versioned execution artifacts. When disabled, Plans of Record is removed from navigation.',
    disableWarning:
      'Disabling Planning will hide Plans of Record from the sidebar. Existing Plan Cycles and versions are retained in the database.',
    status: 'live',
    flagKey: 'planning_nav',
    overridePolicy: 'all',
    userDefault: true,
  },
  // ── App Layer — consumer-simple apps (one module+flag pair per app, §2.6).
  // Each gates one /apps/<appId>/* route family. Apps are C-Mode projections
  // over canonical stores/RPCs — disabling an app never touches data.
  {
    key: 'app_session',
    name: 'Session App',
    description:
      'The consumer-simple Session app (/apps/session): facilitators configure a prioritization session and invite people; participants weigh in from any device. Runs on the canonical session machinery.',
    disableWarning:
      'Disabling the Session app hides /apps/session. Sessions, participants, and results are canonical records and are retained; the full platform session surfaces are unaffected.',
    status: 'live',
    group: 'platform',
    defaultEnabled: true,
    flagKey: 'app_session',
    overridePolicy: 'all',
    userDefault: true,
  },
  {
    key: 'app_items',
    name: 'Items App',
    description:
      'The consumer-simple Items app (/apps/items): capture, organize, and track items in one focused surface. Reads and writes the canonical items store.',
    disableWarning:
      'Disabling the Items app hides /apps/items. Items are canonical records and are retained; the full platform item surfaces are unaffected.',
    status: 'live',
    group: 'platform',
    defaultEnabled: true,
    flagKey: 'app_items',
    overridePolicy: 'all',
    userDefault: true,
  },
  {
    key: 'app_viability',
    name: 'Viability App',
    description:
      'The consumer-simple Viability app (/apps/viability): a guided projection of the planning engine — check whether priorities fit capacity, then plan the launch calendar via the three time-phased steps.',
    disableWarning:
      'Disabling the Viability app hides /apps/viability. Scenarios and planning runs are canonical records and are retained; the full planning engine workbench is unaffected.',
    status: 'live',
    group: 'platform',
    defaultEnabled: true,
    flagKey: 'app_viability',
    overridePolicy: 'all',
    userDefault: true,
  },
  {
    key: 'app_focus',
    name: 'Focus App',
    description:
      'The consumer-simple Focus app (/apps/focus): the few things this team is committed to right now, plus the standing pool behind them and a short re-focus ritual. Runs on collections + items.',
    disableWarning:
      'Disabling the Focus app hides /apps/focus. Everything it reads and writes is a canonical record and is retained; the full platform surfaces are unaffected.',
    status: 'live',
    group: 'platform',
    defaultEnabled: true,
    flagKey: 'app_focus',
    overridePolicy: 'all',
    userDefault: true,
  },
  {
    key: 'app_intake',
    name: 'Intake App',
    description:
      'The consumer-simple Intake app (/apps/intake): one mouth for ideas: drop, triage, and an honest status for the submitter. Runs on the canonical items + triage stores.',
    disableWarning:
      'Disabling the Intake app hides /apps/intake. Everything it reads and writes is a canonical record and is retained; the full platform surfaces are unaffected.',
    status: 'live',
    group: 'platform',
    defaultEnabled: true,
    flagKey: 'app_intake',
    overridePolicy: 'all',
    userDefault: true,
  },
  {
    key: 'app_review',
    name: 'Review App',
    description:
      'The consumer-simple Review app (/apps/review): the periodic re-look: what entered, left, or moved since the last published priorities, plus detector findings.',
    disableWarning:
      'Disabling the Review app hides /apps/review. Everything it reads and writes is a canonical record and is retained; the full platform surfaces are unaffected.',
    status: 'live',
    group: 'platform',
    defaultEnabled: true,
    flagKey: 'app_review',
    overridePolicy: 'all',
    userDefault: true,
  },
  {
    key: 'app_classification',
    name: 'Classification App',
    description:
      'The consumer-simple Classification app (/apps/classification): clear the category/altitude proposal queue, accept or reject AI proposals, and confirm classification. Runs on item_classification_proposals + items.',
    disableWarning:
      'Disabling the Classification app hides /apps/classification. Everything it reads and writes is a canonical record and is retained; the full platform item surfaces are unaffected.',
    status: 'live',
    group: 'platform',
    defaultEnabled: true,
    flagKey: 'app_classification',
    overridePolicy: 'all',
    userDefault: true,
  },
  {
    key: 'app_relationships',
    name: 'Relationships App',
    description:
      'The consumer-simple Relationships app (/apps/relationships): review AI Nexus relationship suggestions and inspect how items connect. Runs on nexus_relationship_proposals + item relationship edges.',
    disableWarning:
      'Disabling the Relationships app hides /apps/relationships. Everything it reads and writes is a canonical record and is retained; the full platform relationship surfaces are unaffected.',
    status: 'live',
    group: 'platform',
    defaultEnabled: true,
    flagKey: 'app_relationships',
    overridePolicy: 'all',
    userDefault: true,
  },
  {
    key: 'app_align',
    name: 'Align App',
    description:
      'The consumer-simple Align app (/apps/align): objectives and the work standing behind them, with the gaps called out. Reads the strategy-alignment layer and Compass findings.',
    disableWarning:
      'Disabling the Align app hides /apps/align. Everything it reads and writes is a canonical record and is retained; the full platform surfaces are unaffected.',
    status: 'live',
    group: 'platform',
    defaultEnabled: true,
    flagKey: 'app_align',
    overridePolicy: 'all',
    userDefault: true,
  },
  {
    key: 'app_rank',
    name: 'Rank App',
    description:
      'Retired 2026-10-05: Rank was the Session app under a second name, and /apps/rank now opens Session. Kept on record (DEP-029).',
    disableWarning:
      'Rank is retired — /apps/rank opens the Session app whether this is on or off. Ranking sessions and their results are canonical records and are retained.',
    status: 'live',
    group: 'platform',
    defaultEnabled: true,
    flagKey: 'app_rank',
    overridePolicy: 'all',
    userDefault: true,
  },
  {
    key: 'app_cut',
    name: 'Cut App',
    description:
      'The consumer-simple Cut app (/apps/cut): S2 cut-line only — order candidates, draw the cut, and save the list as a durable item pool with the cut line in place.',
    disableWarning:
      'Disabling the Cut app hides /apps/cut. Item pools created from cuts are canonical records and are retained; the full platform list surfaces are unaffected.',
    status: 'live',
    group: 'platform',
    defaultEnabled: true,
    flagKey: 'app_cut',
    overridePolicy: 'all',
    userDefault: true,
  },
  {
    key: 'app_allocate',
    name: 'Allocate App',
    description:
      'The consumer-simple Allocate app (/apps/allocate): split a finite envelope across competing asks and show the work — Buy-a-Feature by default, Cost-of-Delay when the split must be defensible. Runs on the canonical session machinery.',
    disableWarning:
      'Disabling the Allocate app hides /apps/allocate. Allocation sessions and their results are canonical records and are retained; the full platform session surfaces are unaffected.',
    status: 'live',
    group: 'platform',
    defaultEnabled: true,
    flagKey: 'app_allocate',
    overridePolicy: 'all',
    userDefault: true,
  },
  {
    key: 'app_offsite',
    name: 'Offsite App',
    description:
      'The consumer-simple Offsite app (/apps/offsite): collect the decisions an offsite must make, run the room with present mode plus a phone in every hand, and leave with a record of what was decided, what was parked, and who owns what. Wraps a pre-designed event; the B-Mode event designer stays in the full platform.',
    disableWarning:
      'Disabling the Offsite app hides /apps/offsite. Offsite sessions, their results and any commitments recorded are canonical records and are retained.',
    status: 'live',
    group: 'platform',
    defaultEnabled: true,
    flagKey: 'app_offsite',
    overridePolicy: 'all',
    userDefault: true,
  },
  {
    key: 'app_hire',
    name: 'Hire App',
    description:
      'The consumer-simple Hire app (/apps/hire): a hiring panel scores candidates against agreed criteria independently, before the debrief, so discussion starts from real judgement rather than whoever spoke first.',
    disableWarning:
      'Disabling the Hire app hides /apps/hire. Hiring sessions and their scored results are canonical records and are retained.',
    status: 'live',
    group: 'platform',
    defaultEnabled: true,
    flagKey: 'app_hire',
    overridePolicy: 'all',
    userDefault: true,
  },
  {
    key: 'app_vendor_select',
    name: 'Vendor Select App',
    description:
      'The consumer-simple Vendor Select app (/apps/vendor-select): compare vendors on weighted criteria and keep the scored trail procurement and legal ask for.',
    disableWarning:
      'Disabling the Vendor Select app hides /apps/vendor-select. Vendor comparisons and their results are canonical records and are retained.',
    status: 'live',
    group: 'platform',
    defaultEnabled: true,
    flagKey: 'app_vendor_select',
    overridePolicy: 'all',
    userDefault: true,
  },
  {
    key: 'app_risk_triage',
    name: 'Risk Triage App',
    description:
      'The consumer-simple Risk Triage app (/apps/risk-triage): plot risks by impact against effort to mitigate, separate mitigate-now from watch, and give the top group owners.',
    disableWarning:
      'Disabling the Risk Triage app hides /apps/risk-triage. Triage sessions, their results and any commitments recorded are canonical records and are retained.',
    status: 'live',
    group: 'platform',
    defaultEnabled: true,
    flagKey: 'app_risk_triage',
    overridePolicy: 'all',
    userDefault: true,
  },
  {
    key: 'app_post_mortem',
    name: 'Post-Mortem App',
    description:
      'The consumer-simple Post-Mortem app (/apps/post-mortem): collect contributing factors, let the room dot-vote what mattered, and leave with owners on the fixes.',
    disableWarning:
      'Disabling the Post-Mortem app hides /apps/post-mortem. Review sessions, their results and any commitments recorded are canonical records and are retained.',
    status: 'live',
    group: 'platform',
    defaultEnabled: true,
    flagKey: 'app_post_mortem',
    overridePolicy: 'all',
    userDefault: true,
  },
  {
    key: 'app_ask_the_room',
    name: 'Ask the Room App',
    description:
      'The consumer-simple Ask the Room app (/apps/ask-the-room): put a question and its options up, and get an anonymous read from a group in about two minutes.',
    disableWarning:
      'Disabling the Ask the Room app hides /apps/ask-the-room. Questions asked and their results are canonical records and are retained.',
    status: 'live',
    group: 'platform',
    defaultEnabled: true,
    flagKey: 'app_ask_the_room',
    overridePolicy: 'all',
    userDefault: true,
  },
  {
    key: 'app_decisions',
    name: 'Decisions App',
    description:
      'The consumer-simple Decisions app (/apps/decisions): browse every memorialized decision and answer "why did we do this?" with a link. Read-only projection of the canonical decisions record.',
    disableWarning:
      'Disabling the Decisions app hides /apps/decisions. Decision records are canonical and are retained; the full platform Decision Log is unaffected.',
    status: 'live',
    group: 'platform',
    defaultEnabled: true,
    flagKey: 'app_decisions',
    overridePolicy: 'all',
    userDefault: true,
  },
  {
    key: 'app_first_100_days',
    name: 'First 100 Days App',
    description:
      'The First 100 Days app (/apps/first-100-days): the hundred-day journey for a leader new to a role, on Mandate\'s record. Listening-tour notes on stakeholders, priorities as items with a disposition, and a Day 101 ranking recorded as a decision.',
    disableWarning:
      'Disabling the First 100 Days app hides /apps/first-100-days. Its notes, items, ranking and decision stay on Mandate\'s record and the shared record.',
    status: 'live',
    group: 'platform',
    defaultEnabled: true,
    flagKey: 'app_first_100_days',
    overridePolicy: 'all',
    userDefault: true,
  },
  {
    key: 'app_whats_next',
    name: 'What\'s Next App',
    description:
      'The What\'s Next app (/apps/whats-next): Morning Boot, a daily ritual on the person\'s own items. Archiving, tags, a personal Stack Ranking session and a commitment due today, all on the shared record.',
    disableWarning:
      'Disabling the What\'s Next app hides /apps/whats-next. Items, rankings and commitments it made are retained.',
    status: 'live',
    group: 'platform',
    defaultEnabled: true,
    flagKey: 'app_whats_next',
    overridePolicy: 'all',
    userDefault: true,
  },
  {
    key: 'app_mandate',
    name: 'Mandate App',
    description:
      'The Mandate app (/apps/mandate): transition governance for a leader new to a role. Situation, the 100-day outcome, a priorities stack, stakeholders and a decision record, on the shared record (items, decisions and sessions tagged origin_module = mandate).',
    disableWarning:
      'Disabling the Mandate app hides /apps/mandate. Transitions, priorities, stakeholders and decisions are retained; items and decisions it mirrored stay on the shared record.',
    status: 'live',
    group: 'platform',
    defaultEnabled: true,
    flagKey: 'app_mandate',
    overridePolicy: 'all',
    userDefault: true,
  },
  {
    key: 'app_session_import',
    name: 'Record App (session-results import)',
    description:
      'The consumer-simple Record app (/apps/session-import): import the results of a prioritization run outside the platform — a ranked list or a full scoring workbook — as a completed canonical session. Items link or create through the citizenship doctrine; a post-import Twins sweep proposes duplicates for human review.',
    disableWarning:
      'Disabling the Record app hides /apps/session-import. Sessions and items already imported are canonical records and are retained; the full platform surfaces are unaffected.',
    status: 'live',
    group: 'platform',
    defaultEnabled: true,
    flagKey: 'app_session_import',
    overridePolicy: 'all',
    userDefault: true,
  },
  {
    key: 'app_start',
    name: 'Start (intent-first entry)',
    description:
      'The intent-first Start screen (/apps/start): say what you are trying to do — or pick it from the catalog — and land in the matching app flow with a preset applied. Supersedes the pre-built configuration picker.',
    disableWarning:
      'Disabling Start hides /apps/start and restores the pre-built configuration picker as the home entry point. No data is touched; Start creates nothing of its own.',
    status: 'live',
    group: 'platform',
    defaultEnabled: true,
    flagKey: 'app_start',
    overridePolicy: 'all',
    userDefault: true,
  },
  {
    key: 'stakeholder_pulse',
    name: 'Stakeholder Pulse',
    description:
      'Close the loop with idea submitters after prioritization outcomes are published. Sends structured outcome summaries and collects a simple response — did we address your concern? Builds a feedback quality signal over time.',
    disableWarning:
      'Disabling Stakeholder Pulse will stop outcome summary notifications to submitters and hide the Stakeholder Pulse nav item and pulse response panel in My Submissions. Existing pulse records are retained in the database.',
    status: 'live',
    group: 'platform',
    defaultEnabled: true,
    flagKey: 'stakeholder_pulse',
    overridePolicy: 'all',
    userDefault: true,
  },
  {
    key: 'cost_of_delay',
    name: 'Cost of Delay impact tracking',
    description:
      'Track the real business impact of deferred or deprioritized items — revenue missed, incidents that occurred, churn tied to unresolved issues. Creates calibration data for scoring and makes prioritization decisions defensible over time.',
    disableWarning:
      'Disabling Cost of Delay will hide the Impact tab and the cost-of-delay callout from all item views. Existing impact event records are retained in the database and will reappear if re-enabled.',
    status: 'live',
    group: 'platform',
    defaultEnabled: true,
    flagKey: 'cost_of_delay',
    overridePolicy: 'all',
    userDefault: true,
    // NOTE: `tool_cost_of_delay` (group 'tools') is a DIFFERENT key — it
    // shows/hides the Cost of Delay scoring tool, and toggling it does not
    // touch this one. Listed in Modules settings so an admin can switch the
    // Impact tab off (owner ruling 2026-10-14: every module has a switch).
  },
  {
    key: 'item_certainty_reading',
    name: 'How sure we are: what the record shows',
    description:
      'On the item page, under How sure we are, a reading of how well each item is known, worked out from what is on the record: the evidence linked to it and how reliable each piece is, how many people contributed values and whether they disagree, where the effort number came from, and whether an agent created it without a review. Shown beside the typed knowledge status and confidence, with each missing thing and one action to close it. Nothing is stored; it is read off the record each time.',
    disableWarning:
      'The reading and its gap rows disappear from the item page. The typed Planning maturity, Knowledge status and Confidence rows stay.',
    status: 'live',
    group: 'platform',
    // Off until the owner says it is ready (owner, 2026-10-15: "keep it behind a
    // module because I don't want it to show until it's ready"). The 2026-10-14
    // default-on ruling resumes when he lifts this.
    defaultEnabled: false,
    overridePolicy: 'none',
  },
  {
    key: 'delivery_tracking',
    name: 'Delivery Tracking',
    description:
      'Record what actually shipped after prioritization — delivery type, predicted vs actual scores, and lessons learned. Powers the Calibration dashboard for scoring accuracy over time.',
    disableWarning:
      'Disabling Delivery Tracking will hide the Delivery tab on items and the Calibration report under Reports. Existing delivery records are retained in the database.',
    status: 'live',
    group: 'platform',
    defaultEnabled: true,
    flagKey: 'delivery_tracking',
    overridePolicy: 'all',
    userDefault: true,
  },
  {
    key: 'execution_recipe',
    name: 'Work Type Approach',
    description:
      'Show how each work type is approached on item pre-planning — approach kind, uncertainty multiplier, required documents, and governance policies. Includes the Work Types settings catalog.',
    disableWarning:
      'Turning off Work Type Approach hides the approach panel from item pre-planning and the Work Types settings page. Work type assignment on items remains available; existing work type rows are kept.',
    status: 'live',
    group: 'platform',
    defaultEnabled: true,
    flagKey: 'execution_recipe',
    overridePolicy: 'all',
    userDefault: true,
  },
  {
    key: 'judgment_index',
    name: 'Judgment Index',
    description:
      'Hartman Judgment Index profiles for workspace members — advisory context for how people weigh intrinsic, extrinsic, and systemic dimensions. Never alters rankings or scores.',
    disableWarning:
      'Disabling Judgment Index will hide the Judgment Index nav item and profile surfaces. Existing profiles are retained in the database.',
    status: 'live',
    group: 'preview',
    defaultEnabled: true,
    flagKey: 'judgment_index',
    overridePolicy: 'all',
    userDefault: true,
  },
  {
    key: 'method_intro',
    name: 'Method Intro',
    description:
      'First-run teaser and guided tour connecting Managing Priorities (DEGAP) to where each phase happens in the app. The permanent /method reference pages (phase explanations, object types, technique toolbox) are always reachable from navigation regardless of this module — it only gates the teaser and the /method/tour walkthrough.',
    disableWarning:
      'Disabling Method Intro hides the first-run teaser and redirects /method/tour to /method. The /method reference pages are unaffected.',
    status: 'live',
    group: 'preview',
    defaultEnabled: true,
    flagKey: 'method_intro',
    overridePolicy: 'all',
    userDefault: true,
  },
  {
    key: 'group_decisions',
    name: 'Group decisions',
    description:
      'Let a small group score options together from a shared link. Not ready for general use yet.',
    disableWarning:
      'Turning off Group decisions hides the Group decisions card, its home widget, and the way to start a new one. Existing group decisions are kept, and anyone with a join link can still use it.',
    status: 'live',
    group: 'preview',
    defaultEnabled: true,
    flagKey: 'group_decisions',
    overridePolicy: 'all',
    // One switch: when a workspace turns it on, every member sees it.
    userDefault: true,
  },
  {
    key: 'decision_brief',
    name: 'Decision Log',
    description:
      'Frame a decision, collect proposals from option owners, compare, decide, and share the record.',
    disableWarning:
      'Turning off the Decision Log hides it from the sidebar and the Lab, and its pages send people to Decisions instead. Existing decisions are kept, and links already sent to option owners or shared as a record still work.',
    status: 'live',
    group: 'preview',
    defaultEnabled: true,
    flagKey: 'decision_brief',
    overridePolicy: 'all',
    // One switch: when a workspace turns it on, every member sees it.
    userDefault: true,
  },
  // Owner ask 2026-10-14: everything under Decisions in the sidebar can be
  // switched off on its own. Decision Log and Judgment Analytics already could;
  // these three give Decision Intel, Bias Audit and Calibration their own switch.
  {
    key: 'decision_intelligence',
    name: 'Decision Intel',
    description:
      'Decision intelligence: the signals and observations gathered about how decisions are made, shown under Decisions in the sidebar.',
    disableWarning:
      'Turning off Decision Intel hides it from the sidebar and its page sends people to Decisions. Captured signals and observations are kept.',
    status: 'live',
    group: 'platform',
    defaultEnabled: true,
    overridePolicy: 'all',
    userDefault: true,
  },
  {
    key: 'bias_audit',
    name: 'Bias Audit',
    description:
      'The cognitive bias audit: how known biases show up in this workspace’s cycles and sessions, shown under Decisions in the sidebar.',
    disableWarning:
      'Turning off Bias Audit hides it from the sidebar and its page sends people to Decisions. Nothing is deleted.',
    status: 'live',
    group: 'platform',
    // Off by default for every workspace (owner, 2026-11-02: "I want the bias
    // audit module to be default off for everyone"). Stays switchable and listed.
    defaultEnabled: false,
    overridePolicy: 'all',
    userDefault: true,
  },
  {
    key: 'decision_calibration',
    name: 'Calibration',
    description:
      'How estimates compared with what actually happened, shown under Decisions in the sidebar. Also needs Delivery Tracking on.',
    disableWarning:
      'Turning off Calibration hides it from the sidebar and its page sends people to Decisions. Delivery records are kept.',
    status: 'live',
    group: 'platform',
    defaultEnabled: true,
    overridePolicy: 'all',
    userDefault: true,
    requires: 'delivery_tracking',
  },
  {
    key: 'judgment_analytics',
    name: 'Judgment Analytics',
    description:
      'Decision-intelligence and judgment-taxonomy analytics — calibration, non-consensus edge, judgment-lens profiles, and a leader trust map for deciding whose judgment to weight on which kind of call. Advisory only — never alters rankings or authority state. Requires the Judgment Analytics entitlement.',
    disableWarning:
      'Disabling Judgment Analytics will hide its nav item and analytics surfaces. Captured signals, outcomes, and derived judgment scores are retained in the database.',
    status: 'live',
    group: 'preview',
    defaultEnabled: true,
    flagKey: 'judgment_analytics',
    overridePolicy: 'all',
    userDefault: true,
  },
  {
    key: 'lexicon',
    name: 'Lexicon',
    description:
      'Language intelligence from meeting transcripts — per-speaker NLP meta-model profiles ("baseball cards"). Experimental.',
    disableWarning:
      'Disabling Lexicon will hide the Lexicon nav item and transcript analysis surfaces. Existing transcript and profile records are retained in the database.',
    status: 'live',
    group: 'preview',
    defaultEnabled: true,
    flagKey: 'lexicon',
    overridePolicy: 'all',
    userDefault: true,
  },
  {
    key: 'commitment_registry',
    name: 'Commitment Registry',
    description:
      'Track commitments made to customers, partners, or regulators as first-class artifacts with due dates, owners, and linked items. Surfaces which active commitments are covered by current cycle priorities and which are orphaned.',
    disableWarning:
      'Disabling Commitment Registry will hide the commitments surface across item and planning views. Existing commitment records are retained in the database.',
    status: 'live',
    flagKey: 'commitment_registry',
    overridePolicy: 'all',
    userDefault: true,
  },
  {
    key: 'requests_and_agreements',
    name: 'Requests & Agreements',
    description:
      'Our recommended way to ask for work and answer. A request names who is asking, who is asked, what they will do, how anyone will know it is done, and by when. The person asked accepts, declines, makes a counteroffer or says when they will answer, and can renegotiate a promise they can no longer keep. When it is done, they report it and the person who asked confirms it. Works on assignments in the Commitment Registry.',
    disableWarning:
      'Turning off Requests & Agreements leaves assignments with Accept alone: no decline, counteroffer or renegotiation, and the person assigned marks their own work fulfilled. Responses already open can still be answered. Nothing is deleted.',
    status: 'live',
    group: 'platform',
    defaultEnabled: true,
    overridePolicy: 'all',
    userDefault: true,
  },
  {
    key: 'delegation',
    name: 'Delegation',
    description:
      'Our recommended way to hand work over: every request says how much is delegated, on the Bounded Agency ladder from 1, Execute Precisely, to 7, Own the Outcome. How risky the work is and whether it can be undone suggest the level; the request says what the person may do, what they must not, when to come back, and how to report. They can escalate when a boundary is hit, and the level shapes what comes back: a recommendation to approve at 3, a draft to release at 4. Rides on Requests & Agreements.',
    disableWarning:
      'Turning off Delegation stops new requests from naming a level and removes Escalate. Requests already made keep their level, and an escalation already open can still be answered. Nothing is deleted.',
    status: 'live',
    group: 'platform',
    defaultEnabled: true,
    overridePolicy: 'all',
    userDefault: true,
    requires: 'requests_and_agreements',
  },
  {
    key: 'backlog_health',
    name: 'Backlog Health',
    description:
      'Backlog health scoring, diagnostics, and actionable recommendations for improving item quality and readiness. Surfaces patterns across your queue — items missing owners, stale proposals, low-evidence candidates — and suggests where to focus cleanup effort.',
    disableWarning:
      'Disabling Backlog Health will hide the Backlog Health settings page and remove its nav entry. Existing health records are retained.',
    status: 'live',
    group: 'platform',
    defaultEnabled: true,
    flagKey: 'backlog_health',
    overridePolicy: 'all',
    userDefault: true,
  },
  {
    key: 'cycle_phase_control',
    name: 'Cycle Phase',
    description:
      "On a cycle's settings, the Cycle phase panel: the cycle's DEGAP phase (Decide, Engage, Generate, Agree, Publish) and a button to move it one step at a time.",
    disableWarning:
      "The Cycle phase panel disappears from the cycle's settings. A cycle's phase is kept and nothing about it changes.",
    status: 'live',
    group: 'platform',
    // Not part of the product at present (owner, 2026-11-03: "make it a module
    // because we might build on it later. But it should be default off for
    // everyone"). The 2026-10-14 default-on ruling does not apply to it.
    defaultEnabled: false,
    overridePolicy: 'none',
  },
  {
    key: 'cycle_focus_mode',
    name: 'Cycle Focus Mode',
    description:
      'Lets a member pin one cycle as their active context, so navigation and lists narrow to that cycle until they exit. Adds a Focus control on cycle detail and a pin control on the cycle list.',
    disableWarning:
      'Disabling Focus Mode hides the controls that ENTER focus. Anyone already focused keeps the controls that exit it, so nobody is stranded in a narrowed view.',
    status: 'live',
    group: 'platform',
    // On by default like every module (owner ruling 2026-10-14).
    defaultEnabled: true,
  },
  {
    key: 'design_hub',
    name: 'Design Hub',
    description:
      'Centralized hub for design processes — linking design artifacts, managing design reviews, and connecting design decisions to prioritized items.',
    disableWarning:
      'Disabling Design Hub will hide the Design Hub nav item. Existing design artifacts are retained.',
    status: 'live',
    group: 'platform',
    defaultEnabled: true,
    flagKey: 'design_hub',
    overridePolicy: 'all',
    userDefault: true,
  },
  {
    key: 'elevation_platform',
    // Shown as "Team pages" (Wes, 2026-10-18). The key and flagKey are stored
    // (workspaces.features, the flag rows) and stay. The copy says what the
    // switch opens under the three access levels in lib/elevationAccess.ts.
    name: 'Team pages',
    description:
      "Opens the team hub (/home) and the team pages behind it: Design Language, the Lab, the Method and the Workbook, World, and the product roadmap. They are for the Peak Priorities team only. Elevation's own pages, the knowledge system, open for their owner only. Nobody else sees any of it, with this on or off.",
    disableWarning:
      "Turning off Team pages sends the team hub, the team pages and Elevation's own pages to the prioritization dashboard for everyone in this workspace. No content is deleted.",
    status: 'live',
    group: 'platform',
    defaultEnabled: true,
    flagKey: 'elevation_nav',
    overridePolicy: 'all',
    userDefault: true,
    // Customers never see this row or its flag (Wes, 2026-10-18).
    elevationLevel: 'team',
  },
  {
    key: 'strategy_canvas',
    name: 'Strategy Canvas',
    description:
      'One-page strategy communication framework. Ties together Outcome Drivers, Vision, Purpose, Mission, Guiding Principles, Competencies, and Assumptions into a single graphical canvas for communicating complex change. Multiple canvases per workspace.',
    disableWarning:
      'Disabling Strategy Canvas will hide the Strategy Canvas nav item. Existing canvas records are retained in the database and will be visible again if re-enabled.',
    status: 'live',
    group: 'platform',
    defaultEnabled: true,
    flagKey: 'strategy_canvas_nav',
    overridePolicy: 'all',
    userDefault: true,
    requires: 'strategy',
  },
  {
    key: 'v2mom',
    name: 'V2MOM',
    description:
      'Vision, Values, Methods, Obstacles, Measures — the one-page alignment document every leader writes against the one above them. Documents chain CEO → SVP → VP → director through a parent link; methods can point at Strategic Objectives.',
    disableWarning:
      'Disabling V2MOM will hide the V2MOM nav item. Existing documents are retained in the database and will be visible again if re-enabled.',
    status: 'live',
    group: 'platform',
    defaultEnabled: true,
    flagKey: 'v2mom_nav',
    overridePolicy: 'all',
    userDefault: true,
    requires: 'strategy',
  },
  {
    key: 'plan_drift',
    name: 'Plan Drift Detection',
    description:
      'Detect when Plans of Record diverge from their source Published Priority Lists. Surfaces sequencing changes and flags items that were added, removed, or reordered relative to the original prioritization output.',
    disableWarning:
      'Disabling Plan Drift Detection will hide drift indicators from plan views. No plan data is deleted.',
    status: 'live',
    group: 'platform',
    defaultEnabled: true,
    flagKey: 'plan_drift_detection',
    overridePolicy: 'all',
    userDefault: true,
    requires: 'planning',
  },

  // ── Needs wiring — feature exists, module toggle not yet implemented ──────
  {
    key: 'catchball',
    name: 'Catchball',
    description:
      'Formal proposal and loop workbench for structured back-and-forth between teams. When disabled, the Catchball section is removed from navigation, all Catchball routes are blocked, and proposal submission is hidden from session results.',
    disableWarning:
      'Disabling Catchball will hide the Catchball section in the sidebar, block all Catchball pages, and remove the proposal formation button from session results. No Catchball data is deleted.',
    status: 'live',
    group: 'platform',
    defaultEnabled: true,
  },
  {
    key: 'tournaments',
    name: 'Tournaments',
    description:
      'Multi-round tournaments. When disabled, the Tournaments nav and pages, the tournament buttons on a track, and the Tournament step in cycle configuration are hidden.',
    disableWarning:
      'Disabling Tournaments hides the Tournaments pages, track buttons and the cycle-configuration Tournament step. Existing tournament results are retained.',
    status: 'live',
    defaultEnabled: true,
  },
  {
    key: 'intake_embed',
    name: 'Intake Embed',
    description:
      'Embeddable idea submission forms that can be deployed externally. When disabled, embed token generation is hidden and public submission endpoints are inactive.',
    disableWarning:
      'Disabling Intake Embed will hide embed token management in Queue settings. Existing tokens will be deactivated.',
    status: 'live',
    defaultEnabled: true,
  },
  {
    key: 'evidence',
    name: 'Evidence',
    description:
      'Attach supporting evidence, research, and artifacts to items and decisions. When disabled, evidence attachment is hidden across the platform.',
    disableWarning:
      'Disabling Evidence will hide evidence panels on items and decisions. Existing evidence records are retained in the database.',
    // NOTE: The 'evidence_enforcement' feature flag controls enforcement strictness
    // (whether proposals are blocked without evidence). That is a behavioral flag,
    // not an access flag, and is intentionally separate from this module.
    status: 'live',
    defaultEnabled: true,
  },
  {
    key: 'org_hierarchy',
    name: 'Org Hierarchy',
    description:
      'Model your organization as a tree of units, teams, and queues. When disabled, the Org Hierarchy settings page is hidden and the "Route to Queue" option is removed from idea submission.',
    disableWarning:
      'Disabling Org Hierarchy will hide the org structure settings and queue routing on the idea submission form. Existing org unit data is retained in the database.',
    status: 'live',
    defaultEnabled: true,
  },
  {
    key: 'attributes',
    name: 'Custom Fields',
    description:
      'Custom data fields attached to items. When disabled, custom field panels are hidden from item views and the Custom Fields settings page is removed.',
    disableWarning:
      'Disabling Custom Fields will hide custom field panels from all item views and remove the Custom Fields settings page. Existing field values are retained in the database and will reappear if re-enabled.',
    status: 'live',
    defaultEnabled: true,
  },
  {
    key: 'reports',
    name: 'Reports',
    description:
      'Cycle execution reports and custom reports. When disabled, the Reports section is removed from navigation.',
    disableWarning:
      'Disabling Reports will hide the Reports section in the sidebar. Existing report data is retained in the database.',
    status: 'live',
    defaultEnabled: true,
  },
  {
    key: 'decision_rights',
    name: 'Decision Rights',
    description:
      'Three-axis governance model defining who decides what, how, and who decides who decides. Relevant for regulated environments and formal governance programs. When disabled, the Decision Rights panel is hidden from cycle workspaces.',
    disableWarning:
      'Disabling Decision Rights will hide the Decision Rights panel from cycle workspaces. Existing decision right records and governance events are retained in the database.',
    status: 'live',
    defaultEnabled: true,
  },

  // ── UI Previews — new designs that workspace admins can enable workspace-wide ──
  // Like every module, previews start on and a workspace admin can switch
  // them off (owner ruling 2026-10-14).
  {
    key: 'input_rounds',
    name: 'Input rounds',
    description:
      'Crowd input rounds: collect structured input on items from stakeholders before a prioritization cycle — open, close and reveal a round, with a blind mode that hides others\' answers. Off until a workspace admin switches it on (owner, 2026-10-16: not shown until it is ready).',
    disableWarning:
      'Switching off Input rounds hides its pages and its settings entry. Rounds and the input already collected are kept.',
    status: 'live',
    group: 'platform',
    defaultEnabled: false,
    overridePolicy: 'none',
  },
  {
    key: 'extended_navigation',
    name: 'Extended Navigation',
    description:
      'Exposes advanced navigation surfaces: Scenario Planner, Item Pools, Executive Inbox, Proposals, Ratification, Commitments, Decisions, and Decision Intel. When disabled, these items are hidden from the sidebar to reduce noise for standard workspaces.',
    disableWarning:
      'Disabling Extended Navigation will hide Scenario Planner, Item Pools, Executive Inbox, Proposals, Ratification, Commitments, Decisions, and Decision Intel from the sidebar. No data is deleted.',
    status: 'live',
    group: 'platform',
    defaultEnabled: true,
    overridePolicy: 'none',
  },
  {
    key: 'contributor_role_nav',
    name: 'Role-based member navigation',
    description:
      'Shapes the sidebar and home defaults by job role for non-admins (MY WORK · OUTPUTS · WORKSPACE). This does not turn apps on or off — it only changes how navigation is organized. Admins always keep the full operating-domain sidebar. Disable to show every member the full sidebar (still filtered by permissions).',
    disableWarning:
      'Disabling Role-based member navigation shows the full operating-domain sidebar to all members. Role-based home dashboard defaults that depend on the contributor shell are also turned off.',
    status: 'live',
    group: 'platform',
    defaultEnabled: true,
    flagKey: 'contributor_role_nav',
    overridePolicy: 'all',
    userDefault: true,
  },
  {
    key: 'cycle_designer_v2',
    name: 'Cycle Designer V2',
    description:
      'When enabled, new cycles use the native Cycle Designer wizard to define Catchball paths and Tracks before track configuration.',
    disableWarning:
      'Disabling Cycle Designer V2 returns to the guided setup wizard and traditional Cycle Detail page.',
    status: 'live',
    group: 'preview',
    defaultEnabled: true,
    flagKey: 'cycle_designer_v2',
    overridePolicy: 'all',
    userDefault: true,
  },
  {
    key: 'track_workspace_v2',
    name: 'Track Workspace V2',
    description:
      'When enabled, opening a Track uses the new workspace design — a conditions-met layout with a sidebar navigator — instead of the traditional tabbed detail page.',
    disableWarning:
      'Disabling Track Workspace V2 returns to the original tabbed detail page.',
    status: 'live',
    group: 'preview',
    // On by default (owner ruling 2026-10-14). Keep in step with
    // FLAG_CONFIGS.defaultEnabled for the paired flag.
    defaultEnabled: true,
    flagKey: 'track_workspace_v2',
    overridePolicy: 'all',
    userDefault: true,
  },
  {
    key: 'story_overlay',
    name: 'Story overlay',
    description:
      'When enabled, the track page says why its one thing to do now matters, in a sentence under the call to action. Where the track is in the work and the one thing to do now are part of every track page either way. Built for sales and education workspaces; switch it off where it is not wanted.',
    disableWarning:
      'Disabling the Story overlay leaves the track page’s call to action without the sentence that says why. Nothing is lost.',
    status: 'live',
    group: 'platform',
    // On by default like every module (owner ruling 2026-10-14); the overlay
    // is meant for sales and education workspaces (ruling 2026-10-20), so a
    // workspace that does not want it switches it off. Keep in step with
    // FLAG_CONFIGS for the paired flag.
    defaultEnabled: true,
    flagKey: 'story_overlay',
    overridePolicy: 'all',
    userDefault: true,
  },
  {
    key: 'agents_as_users',
    name: 'Agents as Users (Voices)',
    description:
      'When enabled, participant-standing Voices can be invited to async sessions as advisory contributors — badged, sponsor-owned, never authority.',
    disableWarning:
      'Disabling Agents as Users hides Voices from Settings and session participant pickers. Existing Voice seats and advisory responses are retained.',
    status: 'live',
    group: 'preview',
    defaultEnabled: true,
    flagKey: 'agents_as_users',
    overridePolicy: 'all',
    userDefault: true,
  },
  {
    key: 'signal_response',
    name: 'Signal & Response',
    description:
      'When enabled, Plan of Record condition surfaces render the Signal Framework v2 watch band and unified signal log: capture occurrence chips, classify what a signal touches, confirm or dismiss with rationale, and see derived exposure (unanswered signals). No condition letter is selectable anywhere. The underlying signal data layer records regardless.',
    disableWarning:
      'Disabling Signal & Response returns POR surfaces to the legacy read-only condition band. Recorded Signal Events, response links, and expectations are retained — only the UI is hidden.',
    status: 'live',
    group: 'preview',
    defaultEnabled: true,
    flagKey: 'signal_response',
    overridePolicy: 'all',
    userDefault: true,
  },
  {
    key: 'canon_aware_harry',
    name: 'Canon-Aware Harry',
    description:
      'When enabled, the workspace guide (Harry) answers from the layered instruction stack — generated canon law digest, platform charter, agent card — with a surface context packet (route, mode, role, named omissions) instead of the flat prompt. Work lane only; routing behavior unchanged.',
    disableWarning:
      'Disabling Canon-Aware Harry returns the guide to the legacy flat prompt. History, starred answers, and settings are retained.',
    status: 'live',
    group: 'preview',
    defaultEnabled: true,
    flagKey: 'canon_aware_harry',
    overridePolicy: 'all',
    userDefault: true,
  },
  {
    key: 'session_copilot',
    name: 'Session Copilot',
    description:
      'When enabled, session pages carry a facilitation copilot: a deterministic state/stage line plus an assistant that answers "where does this session stand, and what does it allow right now" from the session packet. Work lane only — it offers copyable text drafts (pause note, clarification prompt, divergence framing) and never acts, scores, overrides, or ranks.',
    disableWarning:
      'Disabling the Session Copilot hides the panel from session pages. No data is affected — the copilot never writes anything.',
    status: 'live',
    group: 'preview',
    defaultEnabled: true,
    flagKey: 'session_copilot',
    overridePolicy: 'all',
    userDefault: true,
  },
  {
    key: 'exchange_drafting',
    name: 'Exchange Drafting Aids',
    description:
      'AI drafting help inside Catchball: Loop Intent drafts with a deterministic ambiguity checklist in the Request composer, and proposal rationale/tradeoff drafts in Proposal Formation — always into the local form for the ball-holder to edit and send. Agents draft, never throw: ball movement, submission, and acceptance stay human.',
    disableWarning:
      'Disabling Exchange Drafting hides the "Draft with AI" affordances and the ambiguity checklist from Catchball composers. Loop intent, proposals, and everything already sent are untouched.',
    status: 'live',
    group: 'preview',
    defaultEnabled: true,
    flagKey: 'exchange_drafting',
    overridePolicy: 'all',
    userDefault: true,
  },
  {
    key: 'ambient_runtime',
    name: 'Ambient Runtime',
    description:
      'Background jobs that work while nobody is looking — scheduled connector sync dispatch and a weekly admin digest, delivered per the workspace channel policy. Judgment-lane only: proposes and narrates, never authority.',
    disableWarning:
      'Disabling Ambient Runtime stops all scheduled background jobs and ambient deliveries for this workspace. Job configuration, run history, and the delivery log are retained.',
    status: 'live',
    group: 'preview',
    defaultEnabled: true,
    flagKey: 'ambient_runtime',
    overridePolicy: 'all',
    userDefault: true,
  },
  {
    key: 'semantic_connectors',
    name: 'Semantic Connectors (Excursions)',
    description:
      'Round-trip work with external apps as meaning, not just records: send a collection to a Miro board (an "excursion"), let it evolve there, and bring it home with edits, new items, and relationships preserved — inferred meaning returns as reviewable proposals, never silent writes. Spec: docs/9-exploration/semantic-connectors/.',
    disableWarning:
      'Disabling Semantic Connectors hides the Excursions page and its depart/return flows. Existing excursion records, external-id links, and everything already applied to items remain untouched.',
    status: 'live',
    group: 'preview',
    defaultEnabled: true,
    flagKey: 'semantic_connectors',
    overridePolicy: 'all',
    userDefault: true,
  },
  {
    key: 'record_address',
    name: 'Record Address (external share links)',
    description:
      'Lets the owner, executive sponsor, or a workspace admin mint revocable, scoped, view-only share links so people outside the workspace can read a ratified Plan of Record — the whole record, one version, or one commitment — at its permanent address, with no account. Readers see the frozen record with names and emails stripped. Spec: docs/9-exploration/SPEC-plan-of-record-redesign.md §4 Move 2.',
    disableWarning:
      'Disabling Record Address stops new share links from being created (enforced server-side). Links already issued keep working until withdrawn from the Ratified Record page — withdraw them first if the intent is to close external access.',
    status: 'live',
    group: 'preview',
    defaultEnabled: true,
    flagKey: 'record_address',
    overridePolicy: 'admins_only',
    userDefault: true,
  },
  {
    key: 'query_fed_pools',
    name: 'Query-fed Item Pools',
    description:
      'Lets a pool be fed from a saved query. While the pool is open, "Refresh from feed" shows exactly what the query would add or drop and writes only what a person accepts. At lock the feed is severed and the membership memorialized; items that match afterwards wait in an Arrival Bay, never in the ranked list — every group ranks the same list. Spec: docs/9-exploration/LARGE-SETS-QUERY-AND-SMART-SETS-PRD.md §4.3.',
    disableWarning:
      'Disabling hides the feed panel and the Arrival Bay. Existing feed bindings, held arrivals, and pool membership are unaffected; nothing refreshes on its own either way.',
    status: 'live',
    group: 'preview',
    defaultEnabled: true,
    flagKey: 'query_fed_pools',
    overridePolicy: 'all',
    userDefault: true,
  },
  {
    key: 'pool_amendment',
    name: 'Pool Amendment (mid-cycle)',
    description:
      'Allows the cycle owner or a workspace admin to bring held arrivals into a LOCKED pool with a written reason. It runs as one governed transaction (amend_locked_pool): the single membership is appended, a governance event records before/after, and in-progress sessions on the track are flagged for re-baseline. Without it, late arrivals wait for the next cycle.',
    disableWarning:
      'Disabling refuses new amendments server-side. Amendments already made stay in their pools and in the governance record.',
    status: 'live',
    group: 'preview',
    defaultEnabled: true,
    flagKey: 'pool_amendment',
    overridePolicy: 'admins_only',
    userDefault: true,
  },
  {
    key: 'structure_reorg',
    name: 'Structure Reorganization',
    description:
      'Lets a workspace admin plan a reorganization as a private draft — rename, move, add and remove units without touching the live structure — give every unit that leaves a disposition (merged into, split into, retired), work through what those units still hold, and authorize it as one governed transaction that produces a new major structure version. Standing references carry on a merge; in-flight cycles and catchball finish where they started; permission grants are never moved automatically. Spec: docs/9-exploration/PRD-structure-versioning-and-transitions.md.',
    disableWarning:
      'Disabling hides "Plan a reorganization" and the server refuses new drafts and authorizations. Structure versions already authorized, and their mappings, are retained.',
    status: 'live',
    group: 'preview',
    defaultEnabled: true,
    flagKey: 'structure_reorg',
    overridePolicy: 'admins_only',
    userDefault: true,
  },
  {
    key: 'product_hierarchy',
    name: 'Product Hierarchy',
    description:
      'Model the portfolio as a tree — portfolio, product lines, products, components — as a first-class hierarchy type alongside the org chart, and place items in it. Structural only: placement carries no ranking weight and is not an item relationship. Spec: docs/9-exploration/HIERARCHY-TYPES-ARCHITECTURE-BRIEF.md.',
    disableWarning:
      'Disabling Product Hierarchy hides the Product Hierarchy settings page and the Product chip on items. Product nodes and item placements are retained in the database and reappear if re-enabled.',
    status: 'live',
    group: 'preview',
    defaultEnabled: true,
    flagKey: 'product_hierarchy',
    overridePolicy: 'all',
    userDefault: true,
  },
  {
    key: 'time_phased_planning',
    name: 'Time-Phased Execution Planning',
    description:
      'Launch Calendar and supply-calendar workbench for portfolio capacity: answers whether priorities are buildable vs launchable, surfaces shared-team overcommitment (e.g. Product Marketing), and supports counterfactual levers (promote, delay). Consumes viability planning outputs — does not mutate prioritization state. Spec: docs/9-exploration/TIME-PHASED-EXECUTION-PLANNING-PRD.md.',
    disableWarning:
      'Disabling Time-Phased Execution Planning hides the Launch Calendar and workbench. Existing workload templates, supply calendar rows, and placement runs remain in the database.',
    status: 'live',
    group: 'preview',
    defaultEnabled: true,
    flagKey: 'time_phased_planning',
    overridePolicy: 'all',
    userDefault: true,
    requires: 'planning',
  },
  {
    key: 'plan_narrative',
    name: 'Unified Plan of Record narrative',
    description:
      'Folds the narrative Plan of Record into the plan-cycle record (DEP-026): the cycle’s linked narrative renders as a plan subpage (/planning/plans/:cycleId/narrative) with a summary card on the plan overview, and standalone narrative URLs for cycle-linked records redirect there. Cycle-less org-level narrative records are unaffected. The one-ceremony rule (authorize_por freezes the linked narrative, migration 678) is server-side and does not depend on this module.',
    disableWarning:
      'Disabling hides the plan narrative subpage and restores the standalone narrative instrument links. No records, snapshots, or ceremonies change — the migration-678 ceremony rules keep applying server-side.',
    status: 'live',
    group: 'preview',
    defaultEnabled: true,
    flagKey: 'plan_narrative',
    overridePolicy: 'all',
    userDefault: true,
    requires: 'planning',
  },

  {
    key: 'decision_latency',
    name: 'Decision latency report',
    description:
      'Decomposes "we are slow at decisions" into named stages with numbers: framing, deliberation, proposal, approval queue, resolution, ratification, authorization. Every figure is a subtraction between two timestamps the record already carried — no new stamps, no writes, no ceremonies. Stages whose stamps are missing render as labelled gaps rather than as fast stages, and out-of-order timestamps are excluded rather than averaged in. The authorization segment stays unmeasurable until source_cycle_report_id is wired (migration 678\u2019s deferred note). Spec: docs/9-exploration/DECISION-LATENCY-INSTRUMENTATION-PRD.md.',
    disableWarning:
      'Disabling hides the report only. It is a pure read model over existing timestamps — nothing is stored, computed on write, or changed by turning this off.',
    status: 'live',
    group: 'preview',
    defaultEnabled: true,
    flagKey: 'decision_latency',
    overridePolicy: 'all',
    userDefault: true,
  },

  {
    key: 'conditions',
    name: 'Conditions',
    description:
      'Promotes the conditions a decision or commitment rests on from free text to typed objects: assumptions, numeric thresholds with a stated bound and source, dependencies on other items, and external signals. Each is individually addressable, so surfaces can say "remains our highest commitment while X, Y and Z hold" and name which one moved. Existing free-text reconsideration conditions (migration 464) are untouched and keep rendering alongside. Detection proposes only — a reported reading outside its bound puts a candidate on the review docket; moving a condition, tripping a term and reopening a decision all stay human acts. Spec: docs/9-exploration/CONDITION-MONITORING-AND-DECISION-EXPIRY-PRD.md.',
    disableWarning:
      'Disabling hides condition capture and the condition-health readouts. Nothing is deleted: recorded conditions, observations and their status history are retained and resume rendering if the module is re-enabled. No commitment, term or decision changes state either way.',
    status: 'live',
    group: 'preview',
    defaultEnabled: true,
    flagKey: 'conditions',
    overridePolicy: 'all',
    userDefault: true,
  },

  {
    key: 'commitment_states',
    name: 'Backing (announced vs backed)',
    description:
      'Answers the question the priority ladder never asks: are resources actually bound to this, or was it only announced? A ratified priority reads backed when active resource claims cover its recipe-derived requirement, and announced when they do not \u2014 Wojnowski\u2019s own word, kept because it stings correctly. The state is DERIVED, never stored: nothing is added to proposed \u2192 decided \u2192 ratified \u2192 published, and turning this on changes no record. Coverage is matched per resource type, so engineering hours never cover a design requirement, and where no recipe demand template exists the readout says there is no basis rather than implying coverage. Also carries the displacement question at admission: adding work above the cutline computes the marginal requirement and asks what comes off. Spec: docs/9-exploration/DISPLACEMENT-AND-COMMITMENT-STATES-PRD.md.',
    disableWarning:
      'Disabling hides the badges, the portfolio rollup and the displacement ceremony. No claims, requirements or priority states are deleted or changed \u2014 the state was only ever computed from rows that stay exactly where they are.',
    status: 'live',
    group: 'preview',
    defaultEnabled: true,
    flagKey: 'commitment_states',
    overridePolicy: 'all',
    userDefault: true,
  },

  {
    key: 'comparison_set_validation',
    name: 'Comparison set validation step',
    description:
      'Makes "are these actually N legitimate alternatives?" a step instead of a side panel. Before criteria are finalized, the S3 surface puts the detectors that already run — Twins cluster identity, Nexus structural proposals, and the seven pool anti-patterns — in front of the room in canon Rule 13 order (identity first; anything touching an item whose identity is unresolved is shown but deferred, because a relationship between two items that may be one item cannot be judged). Each finding is confirmed, dismissed, restructured, or cleared by an explicitly recorded human override, and the outcome is appended as one `comparison_set_validated` session event carrying the sentence the step exists to produce: "20 entered; 2 duplicates merged; 16 legitimate alternatives ranked." Accept and dismiss remain the existing accept_twins_cluster / accept_nexus_proposal RPCs — nothing is auto-confirmed, auto-merged or auto-restructured (canon 12 §14.3). Spec: docs/9-exploration/RELATIONSHIP-DETECTION-ASSIST-PRD.md §4.',
    disableWarning:
      'Disabling hides the validation step only. Every detector keeps running and keeps its existing review surfaces, and validation events already recorded are retained — the step reads and records, it does not own any relationship data.',
    status: 'live',
    group: 'preview',
    defaultEnabled: true,
    flagKey: 'comparison_set_validation',
    overridePolicy: 'all',
    userDefault: true,
  },

  {
    key: 'commitment_view',
    name: 'Canonical commitment view',
    description:
      'One read-only page per commitment, at /planning/plans/:cycleId/commitments/:itemId, assembling what is already recorded across seven surfaces: what was committed, why (the decision record\u2019s four-part rationale), instead of what (the below-cutline items it beat), at what cost (declared resource claims), under what conditions (attached stopping rules), how it has changed (the plan-version history with change reasons), and who authorized it. Surfaces that quote a commitment additionally show the version they are quoting and a marker when a newer version has been authorized, so a team can no longer be executing an older reading of a decision without anyone seeing it. Assembly only \u2014 no new write paths, nothing stored, and a section with nothing in it states why rather than rendering blank. Spec: docs/9-exploration/DECISION-PROPAGATION-AND-LINEAGE-PRD.md.',
    disableWarning:
      'Disabling hides the commitment view and the staleness markers. It is a pure read model over records that already exist \u2014 nothing is deleted, and every underlying surface keeps working exactly as it does now.',
    status: 'live',
    group: 'preview',
    defaultEnabled: true,
    flagKey: 'commitment_view',
    overridePolicy: 'all',
    userDefault: true,
    requires: 'planning',
  },

  {
    key: 'resource_roster',
    name: 'Roster & qualified capacity',
    description:
      'Turns anonymous capacity pools into people. People & Capacity gains a Roles view (person / generic role placeholder / external → resource type, hours a week, effective window); a scenario can derive its pools from that roster; and work can declare capability, credential and named-person requirements so a below-the-line answer names the qualification gap instead of saying "no capacity". Rough-cut anonymous pools remain fully supported — every dimension is optional. Spec: docs/9-exploration/PLANNING-RESOURCE-MODEL-AND-VIABILITY-BANDS-PRD.md.',
    disableWarning:
      'Disabling hides the Roles view and returns every scenario to anonymous pools, including scenarios set to roster mode. No resources, role assignments, credentials or requirements are deleted — they resume taking effect if the module is re-enabled.',
    status: 'live',
    group: 'preview',
    defaultEnabled: true,
    flagKey: 'resource_roster',
    overridePolicy: 'all',
    userDefault: true,
    requires: 'planning',
  },

  // ── Tools — individual prioritization tools admins can enable/disable ─────
  // All tools are enabled by default. overridePolicy: 'none' so the workspace
  // decision applies uniformly — there is no individual override for tool access.
  {
    key: 'tool_custom_matrix',
    toolId: 'custom-matrix',
    name: 'Custom 2×2 Matrix',
    description: 'Build and save your own named 2×2 matrix with custom axis labels and quadrant names.',
    disableWarning: 'Custom 2×2 Matrix will be hidden from the Tools page.',
    status: 'live',
    group: 'tools',
    defaultEnabled: true,
    overridePolicy: 'none',
  },
  {
    key: 'tool_ahp',
    toolId: 'ahp',
    adapterNames: ['AHP'],
    name: 'AHP (Analytic Hierarchy Process)',
    description: 'Multi-criteria decision-making using pairwise comparisons and eigenvector math to derive criterion weights. High precision, high effort — best for structured governance contexts.',
    disableWarning: 'AHP will be hidden from the Tools page and from session tool selectors.',
    status: 'live',
    group: 'tools',
    // Switched off in code (owner ruling 2026-10-09) — see switchedOffTools.ts.
    defaultEnabled: false,
    overridePolicy: 'none',
  },
  {
    key: 'tool_dot_voting',
    toolId: 'dot-voting',
    adapterNames: ['DotVoting'],
    name: 'Dot Voting',
    description: 'Participants distribute a fixed number of votes across items. Best for quick group alignment on a large backlog.',
    disableWarning: 'Dot Voting will be hidden from the Tools page and from session tool selectors.',
    status: 'live',
    group: 'tools',
    defaultEnabled: true,
    overridePolicy: 'none',
  },
  {
    key: 'tool_buy_a_feature',
    toolId: 'buy-a-feature',
    adapterNames: ['Buy A Feature'],
    name: 'Buy A Feature',
    description: 'Participants allocate a fake budget to purchase items, revealing willingness-to-pay and group consensus through constrained spending.',
    disableWarning: 'Buy A Feature will be hidden from the Tools page and from session tool selectors.',
    status: 'live',
    group: 'tools',
    defaultEnabled: true,
    overridePolicy: 'none',
  },
  {
    key: 'tool_speed_boat',
    toolId: 'speed-boat',
    adapterNames: ['Speed Boat', 'SpeedBoat', 'SPEED_BOAT'],
    name: 'Speed Boat',
    description: 'Groups identify anchors dragging the boat and engines propelling it — surfacing blockers and accelerators through metaphor.',
    disableWarning: 'Speed Boat will be hidden from the Tools page and from session tool selectors.',
    status: 'live',
    group: 'tools',
    defaultEnabled: true,
    overridePolicy: 'none',
  },
  {
    key: 'tool_pairwise_ranking',
    toolId: 'pairwise-ranking',
    adapterNames: ['Pairwise Ranking'],
    name: 'Pairwise Comparison',
    description: 'Items are compared head-to-head in pairs. Win counts produce a mathematically grounded ranking from relative judgments.',
    disableWarning: 'Pairwise Comparison will be hidden from the Tools page and from session tool selectors.',
    status: 'live',
    group: 'tools',
    defaultEnabled: true,
    overridePolicy: 'none',
  },
  {
    key: 'tool_stack_ranking',
    toolId: 'stack-ranking',
    adapterNames: ['Stack Ranking'],
    name: 'Stack Ranking',
    description: 'Direct forced-order ranking — participants drag or number items into a definitive linear sequence.',
    disableWarning: 'Stack Ranking will be hidden from the Tools page and from session tool selectors.',
    status: 'live',
    group: 'tools',
    defaultEnabled: true,
    overridePolicy: 'none',
  },
  {
    key: 'tool_weighted_scoring',
    toolId: 'simple-weighted-scoring',
    adapterNames: ['Simple Weighted Scoring'],
    name: 'Weighted Scoring',
    description: 'Rate each item against custom criteria with configurable weights. Produces a scored, sortable priority list.',
    disableWarning: 'Weighted Scoring will be hidden from the Tools page and from session tool selectors.',
    status: 'live',
    group: 'tools',
    defaultEnabled: true,
    overridePolicy: 'none',
  },
  {
    key: 'tool_rice_scoring',
    toolId: 'rice-scoring',
    adapterNames: ['RICE'],
    name: 'RICE Scoring',
    description: 'Reach × Impact × Confidence ÷ Effort. A formula-based scoring method for product teams.',
    disableWarning: 'RICE Scoring will be hidden from the Tools page and from session tool selectors.',
    status: 'live',
    group: 'tools',
    defaultEnabled: true,
    overridePolicy: 'none',
  },
  {
    key: 'tool_wsjf',
    toolId: 'wsjf',
    adapterNames: ['WSJF'],
    name: 'WSJF',
    description: 'Weighted Shortest Job First (SAFe). Scores items by Cost of Delay ÷ Job Duration to sequence work that delivers the most value first.',
    disableWarning: 'WSJF will be hidden from the Tools page and from session tool selectors.',
    status: 'live',
    group: 'tools',
    defaultEnabled: true,
    overridePolicy: 'none',
  },
  {
    key: 'tool_cost_of_delay',
    toolId: 'cost-of-delay',
    adapterNames: ['CostOfDelay'],
    name: 'Cost of Delay',
    description: 'Quantify the business impact of deferring each item — revenue missed, churn risk, incident exposure — to make time-sensitivity visible.',
    disableWarning: 'Cost of Delay will be hidden from the Tools page and from session tool selectors.',
    status: 'live',
    group: 'tools',
    defaultEnabled: true,
    overridePolicy: 'none',
  },
  {
    key: 'tool_magic_prioritization',
    toolId: 'magic-prioritization',
    adapterNames: ['MagicPrioritization'],
    name: 'Magic Prioritization',
    description: 'Staged decoupling method that surfaces CD3 — the Cost of Delay Divided by Duration — to sequence work by economic value.',
    disableWarning: 'Magic Prioritization will be hidden from the Tools page and from session tool selectors.',
    status: 'live',
    group: 'tools',
    defaultEnabled: true,
    overridePolicy: 'none',
  },
  {
    key: 'tool_pareto_analysis',
    toolId: 'pareto-analysis',
    adapterNames: ['PARETO'],
    name: 'Pareto Analysis',
    description: 'Apply the 80/20 rule — identify the vital few items that generate the majority of value or resolve the majority of problems.',
    disableWarning: 'Pareto Analysis will be hidden from the Tools page and from session tool selectors.',
    status: 'live',
    group: 'tools',
    defaultEnabled: true,
    overridePolicy: 'none',
  },
  {
    key: 'tool_eisenhower_matrix',
    toolId: 'eisenhower-matrix',
    adapterNames: ['Eisenhower Matrix', 'EISENHOWER_MATRIX'],
    name: 'Eisenhower Matrix',
    description: 'Place items in a 2×2 of Urgent vs. Important to separate what needs immediate action from what needs scheduling, delegation, or dropping.',
    disableWarning: 'Eisenhower Matrix will be hidden from the Tools page and from session tool selectors.',
    status: 'live',
    group: 'tools',
    defaultEnabled: true,
    overridePolicy: 'none',
  },
  {
    key: 'tool_moscow_method',
    toolId: 'moscow-method',
    adapterNames: ['MOSCOW'],
    name: 'MoSCoW Method',
    description: 'Categorize items into Must Have, Should Have, Could Have, and Won\'t Have — a structured scope agreement method.',
    disableWarning: 'MoSCoW Method will be hidden from the Tools page and from session tool selectors.',
    status: 'live',
    group: 'tools',
    defaultEnabled: true,
    overridePolicy: 'none',
  },
  {
    key: 'tool_impact_effort_matrix',
    toolId: 'impact-effort-matrix',
    adapterNames: ['IMPACT_EFFORT_MATRIX'],
    name: 'Impact / Effort Matrix',
    description: 'Plot items on a 2×2 of Impact vs. Effort to identify quick wins, major projects, fill-ins, and thankless tasks.',
    disableWarning: 'Impact / Effort Matrix will be hidden from the Tools page and from session tool selectors.',
    status: 'live',
    group: 'tools',
    defaultEnabled: true,
    overridePolicy: 'none',
  },
  {
    key: 'tool_max_priorities_pyramid',
    toolId: 'max-priorities-pyramid',
    adapterNames: ['Max Priorities Pyramid'],
    name: 'Max Priorities Pyramid',
    description: 'A constrained focus hierarchy — forces teams to commit to fewer top priorities by layering items into a pyramid structure.',
    disableWarning: 'Max Priorities Pyramid will be hidden from the Tools page and from session tool selectors.',
    status: 'live',
    group: 'tools',
    defaultEnabled: true,
    overridePolicy: 'none',
  },
  {
    key: 'tool_fast_vs_right',
    toolId: 'fast-vs-right',
    adapterNames: ['FAST_VS_RIGHT'],
    name: 'Fast vs. Right Framework',
    description: 'Evaluate items on the speed–quality tradeoff spectrum. Surfaces when to move fast and when rigor matters.',
    disableWarning: 'Fast vs. Right Framework will be hidden from the Tools page and from session tool selectors.',
    status: 'live',
    group: 'tools',
    defaultEnabled: true,
    overridePolicy: 'none',
  },
  {
    key: 'tool_prune_product_tree',
    toolId: 'prune-product-tree',
    adapterNames: ['Prune the Product Tree', 'PruneProductTree', 'PRUNE_PRODUCT_TREE'],
    name: 'Prune the Product Tree',
    description: 'Trim features from a product tree canvas — a collaborative game for deciding what to cut, grow, or let wither.',
    disableWarning: 'Prune the Product Tree will be hidden from the Tools page and from session tool selectors.',
    status: 'live',
    group: 'tools',
    defaultEnabled: true,
    overridePolicy: 'none',
  },
];

// ─── Default on, switchable (owner ruling 2026-10-14) ───────────────────────
//
// "Modules should be default on, not required on." Every registered module
// has a switch in Settings → Modules and starts ON: a workspace that never
// set a module (no key in `workspaces.features`) has it on, and a workspace
// admin can turn any module off. This replaces the 2026-09-22 / 2026-09-29
// rulings (most modules always on with no switch; a short list switchable and
// off by default). The database resolver `workspace_module_enabled()` answers
// the same way (migration 916). The one exception is a tool switched off in
// code (SWITCHED_OFF_TOOL_IDS): off everywhere, and not listed.

/**
 * True for a tool module whose tool is switched off in code
 * (SWITCHED_OFF_TOOL_IDS). It is off for every workspace, whatever
 * `workspaces.features` stores, and is not listed in Modules settings.
 */
export function isSwitchedOffModuleKey(moduleKey: string): boolean {
  const mod = WORKSPACE_MODULES.find((m) => m.key === moduleKey);
  return mod?.group === 'tools' && isToolSwitchedOff(mod.toolId);
}

// ─── Tool module lookup helpers ──────────────────────────────────────────────

/**
 * Derive the workspace module key for a given toolId slug (e.g. 'dot-voting' → 'tool_dot_voting').
 * Returns null when no module entry is registered for that toolId (e.g. custom-matrix).
 */
/**
 * The full registry-default features map for a NEW workspace: every
 * registered module key set to its defaultEnabled value.
 *
 * Why explicit values (migration 691): the DB-side gate
 * `workspace_module_enabled()` once treated a missing key as false while the
 * client fell back to the registry default — so a workspace created without a
 * features map showed modules ON in Settings while RLS denied them (the Mosso
 * plan-creation incident, 2026-08-24). Since migration 916 both layers read a
 * missing key as on, so the seed is belt-and-braces: every value is true
 * except a tool switched off in code.
 */
export function defaultWorkspaceFeatures(): Record<string, boolean> {
  // A registry entry with no defaultEnabled is treated as enabled-by-default
  // by the client resolution (getModuleDefaultEnabled: `?? true`) — mirror
  // that here or the seed itself would drop those keys.
  return Object.fromEntries(WORKSPACE_MODULES.map((m) => [m.key, m.defaultEnabled ?? true]));
}

export function getToolModuleKey(toolId: string): string | null {
  const entry = WORKSPACE_MODULES.find((m) => m.group === 'tools' && m.toolId === toolId);
  return entry?.key ?? null;
}

/**
 * Returns true if the given toolId is enabled for the workspace.
 * Tools with no module entry (e.g. custom-matrix) are always considered enabled.
 * A tool switched off in code (SWITCHED_OFF_TOOL_IDS) is never enabled.
 */
export function isToolEnabled(features: Record<string, boolean>, toolId: string): boolean {
  if (isToolSwitchedOff(toolId)) return false;
  const key = getToolModuleKey(toolId);
  if (!key) return true;
  return isModuleEnabled(features, key);
}

/**
 * Returns true if the harmonizer adapter identified by its toolName is enabled.
 * Adapters with no module entry (e.g. OPPORTUNITY_SOLUTION_TREE) are always enabled.
 */
export function isToolEnabledByAdapterName(
  features: Record<string, boolean>,
  adapterName: string
): boolean {
  const entry = toolModuleForAdapterName(adapterName);
  if (!entry) return true;
  return isModuleEnabled(features, entry.key);
}

function toolModuleForAdapterName(adapterName: string): WorkspaceModule | undefined {
  return WORKSPACE_MODULES.find(
    (m) => m.group === 'tools' && m.adapterNames?.includes(adapterName)
  );
}

/**
 * The method/matrix tool id ('dot-voting') for a harmonizer adapter name ('DotVoting').
 *
 * Tool readiness (`evaluate_all_tools_readiness`) is keyed by `tool_prerequisites.tool_name`,
 * which holds adapter names, while the recommendation matrix speaks method tool ids. This
 * table is the one place the two are paired, so compare through it rather than by string.
 * Null when the adapter has no tools module (e.g. OPPORTUNITY_SOLUTION_TREE).
 */
export function toolIdForAdapterName(adapterName: string): string | null {
  return toolModuleForAdapterName(adapterName)?.toolId ?? null;
}

/**
 * Matrix recommendations a person can actually pick: the recommended tool is ready on the
 * cycle (readiness reports it available, under its adapter name) and switched on for the
 * workspace. Returns each with the adapter name the picker selects by. Order follows the
 * recommendation order.
 */
export function recommendedToolsAvailable(
  recommendedToolIds: readonly string[],
  availableAdapterNames: readonly string[],
  features: Record<string, boolean>
): Array<{ toolId: string; adapterName: string }> {
  const out: Array<{ toolId: string; adapterName: string }> = [];
  for (const toolId of recommendedToolIds) {
    if (!isToolEnabled(features, toolId)) continue;
    const adapterName = availableAdapterNames.find((name) => toolIdForAdapterName(name) === toolId);
    if (adapterName) out.push({ toolId, adapterName });
  }
  return out;
}

// ─── Events ──────────────────────────────────────────────────────────────────

/**
 * Dispatched on the window whenever a module toggle or policy change succeeds.
 * Components that render based on module state subscribe to this event.
 */
export const WORKSPACE_MODULES_UPDATED_EVENT = 'workspace-modules-updated';

// ─── DB Reads ────────────────────────────────────────────────────────────────

/**
 * Normalize a single module_policies JSON entry from the DB.
 * RPC writes `user_default` (snake_case); older clients may have written `userDefault`.
 */
export function normalizeModulePolicy(raw: unknown): Partial<ModulePolicy> {
  if (!raw || typeof raw !== 'object') return {};
  const r = raw as Record<string, unknown>;
  const out: Partial<ModulePolicy> = {};
  if (r.override === 'all' || r.override === 'admins_only' || r.override === 'none') {
    out.override = r.override;
  }
  if (typeof r.userDefault === 'boolean') {
    out.userDefault = r.userDefault;
  } else if (typeof r.user_default === 'boolean') {
    out.userDefault = r.user_default;
  }
  return out;
}

/** Normalize the full workspaces.module_policies map to camelCase ModulePolicy fields. */
export function normalizeModulePolicies(
  raw: Record<string, unknown> | null | undefined,
): Record<string, ModulePolicy> {
  if (!raw || typeof raw !== 'object') return {};
  const result: Record<string, ModulePolicy> = {};
  for (const [key, value] of Object.entries(raw)) {
    const n = normalizeModulePolicy(value);
    // Do not invent override/userDefault — getModulePolicy falls back to registry defaults.
    result[key] = {
      ...(n.override !== undefined ? { override: n.override } : {}),
      ...(n.userDefault !== undefined ? { userDefault: n.userDefault } : {}),
    } as ModulePolicy;
  }
  return result;
}

/** Fetch the current module settings and policies for a workspace. */
export async function getWorkspaceModules(workspaceId: string): Promise<{
  data: Record<string, boolean>;
  policies: Record<string, ModulePolicy>;
  workspaceType: WorkspaceType | null;
  error: string | null;
}> {
  const supabase = getSupabaseClient();
  const { data, error } = await supabase
    .from('workspaces')
    .select('features, module_policies, workspace_type')
    .eq('id', workspaceId)
    .single();
  if (error) return { data: {}, policies: {}, workspaceType: null, error: error.message };
  return {
    data: (data?.features as Record<string, boolean>) ?? {},
    policies: normalizeModulePolicies(data?.module_policies as Record<string, unknown> | null),
    workspaceType: (data?.workspace_type as 'personal' | 'team' | 'organizational') ?? null,
    error: null,
  };
}

// ─── DB Writes ───────────────────────────────────────────────────────────────

/**
 * Update a single module toggle on a workspace.
 *
 * The RPC is SECURITY DEFINER and admin-gated: it rejects non-admin
 * callers server-side and emits a governance_events audit row on every
 * successful toggle.
 */
export async function setWorkspaceModule(
  workspaceId: string,
  moduleKey: string,
  enabled: boolean
): Promise<{ error: string | null }> {
  const supabase = getSupabaseClient();
  const { error } = await supabase.rpc('set_workspace_module', {
    p_workspace_id: workspaceId,
    p_module_key: moduleKey,
    p_enabled: enabled,
  });
  if (!error && typeof window !== 'undefined') {
    window.dispatchEvent(
      new CustomEvent(WORKSPACE_MODULES_UPDATED_EVENT, {
        detail: { workspaceId, moduleKey, enabled },
      }),
    );
  }
  return { error: error?.message ?? null };
}

/**
 * Update the override policy and/or user default for a module on a workspace.
 *
 * The RPC is SECURITY DEFINER and admin-gated (migration 364).
 */
export async function setWorkspaceModulePolicy(
  workspaceId: string,
  moduleKey: string,
  policy: Partial<ModulePolicy>
): Promise<{ error: string | null }> {
  const supabase = getSupabaseClient();
  const { error } = await supabase.rpc('set_workspace_module_policy', {
    p_workspace_id: workspaceId,
    p_module_key: moduleKey,
    p_override: policy.override ?? null,
    p_user_default: policy.userDefault ?? null,
  });
  if (!error && typeof window !== 'undefined') {
    window.dispatchEvent(
      new CustomEvent(WORKSPACE_MODULES_UPDATED_EVENT, {
        detail: { workspaceId, moduleKey, policy },
      }),
    );
  }
  return { error: error?.message ?? null };
}

// ─── Resolution helpers ──────────────────────────────────────────────────────

/**
 * Returns whether a module is enabled for the workspace.
 *
 * Resolution order:
 *  0. Tool switched off in code (SWITCHED_OFF_TOOL_IDS) → false, whatever
 *     `workspaces.features` stores
 *  1. Explicit workspace setting in `workspaces.features` (DB) — an admin's
 *     switch
 *  2. Module definition's `defaultEnabled` field (true for every module)
 *  3. `true`
 */
/**
 * Whether Settings lists this module for this person: a team-level module
 * (`elevationLevel: 'team'`) only for the Elevation team. Pass false while it
 * is not yet known who is signed in.
 */
export function isModuleListedFor(mod: WorkspaceModule, elevationTeam: boolean): boolean {
  return elevationTeam || mod.elevationLevel !== 'team';
}

/** Whether Settings → Feature Flags lists this flag for this person: a team-level module's flag only for the team. */
export function isFlagListedFor(flagKey: string, elevationTeam: boolean): boolean {
  const mod = WORKSPACE_MODULES.find((m) => m.flagKey === flagKey);
  return !mod || isModuleListedFor(mod, elevationTeam);
}

export function isModuleEnabled(
  features: Record<string, boolean>,
  moduleKey: string
): boolean {
  if (isSwitchedOffModuleKey(moduleKey)) return false;
  const mod = WORKSPACE_MODULES.find((m) => m.key === moduleKey);
  if (mod?.requires && !isModuleEnabled(features, mod.requires)) return false;
  if (moduleKey in features) return features[moduleKey]!;
  return mod?.defaultEnabled ?? true;
}

/**
 * Canonical gate resolution (build contract §2.7). One resolver for every
 * module-gated surface — the single source of truth for "is this visible?":
 *
 *   1. Module off for the workspace → false. So is a module whose `requires`
 *      module resolves off for this person (checked at both layers).
 *   2. Module has no registered individual flag → true (module applies uniformly).
 *   3. User has an explicit personal flag row AND the module policy lets this
 *      user override → the personal flag wins.
 *   4. Otherwise → the module policy's userDefault (auto-on vs opt-in).
 */
export function resolveModuleGate(
  features: Record<string, boolean>,
  policies: Record<string, ModulePolicy>,
  moduleKey: string,
  isAdmin = false,
): boolean {
  if (!isModuleEnabled(features, moduleKey)) return false;
  const mod = WORKSPACE_MODULES.find((m) => m.key === moduleKey);
  if (mod?.requires && !resolveModuleGate(features, policies, mod.requires, isAdmin)) return false;
  const flagKey = mod?.flagKey as FeatureFlagValue | undefined;
  if (!flagKey || !FLAG_CONFIGS[flagKey]) return true;
  if (hasFlagOverride(flagKey) && canUserOverrideModule(policies, moduleKey, isAdmin)) {
    return isFeatureEnabled(flagKey);
  }
  return resolveUserDefault(policies, moduleKey);
}

/**
 * Dual-gate: workspace module AND individual flag layer.
 *
 * Delegates to `resolveModuleGate` with the cached workspace policies, so the
 * module's override policy and userDefault are honored. The `flagKey` argument
 * is retained for call-site readability but the module registry's own flagKey
 * pairing is what resolves.
 *
 * Note: assumes a non-admin caller for `admins_only` override policies —
 * surfaces that need admin-aware resolution use `useModuleGate` (moduleGate.ts).
 */
export function isModuleFeatureAccessible(
  features: Record<string, boolean>,
  moduleKey: string,
  _flagKey: FeatureFlagValue,
): boolean {
  return resolveModuleGate(features, getCachedWorkspaceModulePolicies(), moduleKey, false);
}

/**
 * Returns the effective policy for a module, merging:
 *  1. Workspace-specific override from module_policies (DB)
 *  2. Module definition static defaults
 *  3. System defaults ('all' override, userDefault per module definition)
 */
export function getModulePolicy(
  policies: Record<string, ModulePolicy>,
  moduleKey: string
): ModulePolicy {
  const dbPolicy = normalizeModulePolicy(policies[moduleKey]);
  const mod = WORKSPACE_MODULES.find((m) => m.key === moduleKey);
  return {
    override: dbPolicy.override ?? mod?.overridePolicy ?? 'all',
    userDefault: dbPolicy.userDefault ?? mod?.userDefault,
  };
}

/**
 * Always On = module enabled + No override + member default On.
 * Personal flag rows are ignored; the feature is required for all members.
 */
export function isAlwaysOnModule(
  features: Record<string, boolean>,
  policies: Record<string, ModulePolicy>,
  moduleKey: string,
): boolean {
  if (!isModuleEnabled(features, moduleKey)) return false;
  const policy = getModulePolicy(policies, moduleKey);
  return policy.override === 'none' && (policy.userDefault ?? true) === true;
}

export interface AdminForcedReenabledFlag {
  moduleKey: string;
  moduleName: string;
  flagKey: FeatureFlagValue;
  featureName: string;
}

/**
 * Modules that are Always On where this user still has an explicit personal
 * opt-out row. Used to show a Feature Flags banner that the admin re-enabled
 * and required the feature. Personal rows are intentionally not deleted.
 */
export function getAdminForcedReenabledFlags(
  features: Record<string, boolean>,
  policies: Record<string, ModulePolicy>,
): AdminForcedReenabledFlag[] {
  const results: AdminForcedReenabledFlag[] = [];
  for (const mod of WORKSPACE_MODULES) {
    if (!mod.flagKey) continue;
    const flagKey = mod.flagKey as FeatureFlagValue;
    const flagConfig = FLAG_CONFIGS[flagKey];
    if (!flagConfig) continue;
    if (!isAlwaysOnModule(features, policies, mod.key)) continue;
    if (!hasFlagOverride(flagKey)) continue;
    if (isFeatureEnabled(flagKey)) continue;
    results.push({
      moduleKey: mod.key,
      moduleName: mod.name,
      flagKey,
      featureName: flagConfig.name,
    });
  }
  return results;
}

/**
 * Returns whether a user (identified by their admin status) is allowed to
 * set a personal feature-flag override for a given module.
 *
 * When false, the user's individual flag row should be ignored and the
 * module's userDefault should be applied instead.
 */
export function canUserOverrideModule(
  policies: Record<string, ModulePolicy>,
  moduleKey: string,
  isAdmin: boolean
): boolean {
  const policy = getModulePolicy(policies, moduleKey);
  if (policy.override === 'none') return false;
  if (policy.override === 'admins_only') return isAdmin;
  return true;
}

/**
 * Returns the individual flag default for a module when the workspace enables it.
 * true  = members have the feature on by default (opt-out model).
 * false = members must enable their own flag (opt-in model).
 */
export function resolveUserDefault(
  policies: Record<string, ModulePolicy>,
  moduleKey: string
): boolean {
  const policy = getModulePolicy(policies, moduleKey);
  return policy.userDefault ?? true;
}

// ─── React hook ──────────────────────────────────────────────────────────────

export type WorkspaceType = 'personal' | 'team' | 'organizational';

type WorkspaceModulesCache = {
  workspaceId: string;
  features: Record<string, boolean>;
  policies: Record<string, ModulePolicy>;
  /** `workspaces.workspace_type` — personal, team or organizational (null until read). */
  workspaceType: WorkspaceType | null;
  /**
   * 'ready'  — features/policies are the workspace's real, server-read values.
   * 'error'  — the fetch failed after retries. Features are empty, but consumers
   *            are unblocked so gates resolve (to registry defaults) instead of hanging forever.
   */
  status: 'ready' | 'error';
};

let workspaceModulesCache: WorkspaceModulesCache | null = null;
const workspaceModulesListeners = new Set<() => void>();

function readCachedWorkspaceModules(workspaceId: string | null | undefined): Record<string, boolean> {
  if (!workspaceId || workspaceModulesCache?.workspaceId !== workspaceId) return {};
  return workspaceModulesCache.features;
}

function readCachedWorkspaceModulePolicies(
  workspaceId: string | null | undefined,
): Record<string, ModulePolicy> {
  if (!workspaceId || workspaceModulesCache?.workspaceId !== workspaceId) return {};
  return workspaceModulesCache.policies;
}

/** Module enablement map for the current workspace from the shared cache ({} until fetched). */
export function getCachedWorkspaceModules(): Record<string, boolean> {
  return readCachedWorkspaceModules(getCurrentWorkspaceId());
}

/** Module policies for the current workspace from the shared cache ({} until fetched). */
export function getCachedWorkspaceModulePolicies(): Record<string, ModulePolicy> {
  return readCachedWorkspaceModulePolicies(getCurrentWorkspaceId());
}

function isWorkspaceModulesCacheReady(workspaceId: string | null | undefined): boolean {
  return Boolean(workspaceId && workspaceModulesCache?.workspaceId === workspaceId);
}

function publishWorkspaceModulesCache(
  workspaceId: string,
  features: Record<string, boolean>,
  policies: Record<string, ModulePolicy>,
  status: 'ready' | 'error' = 'ready',
  workspaceType: WorkspaceType | null = null,
): void {
  workspaceModulesCache = { workspaceId, features, policies, status, workspaceType };
  workspaceModulesListeners.forEach((listener) => listener());
}

function subscribeWorkspaceModulesCache(listener: () => void): () => void {
  workspaceModulesListeners.add(listener);
  return () => workspaceModulesListeners.delete(listener);
}

// ─── Shared loader ───────────────────────────────────────────────────────────

const FETCH_RETRY_DELAYS_MS = [400, 1200];

let inFlightWorkspaceId: string | null = null;

/**
 * Fetch a workspace's modules into the shared cache, once.
 *
 * Deliberate behaviours:
 *  - Deduped across every mounted hook (a page renders many gated surfaces).
 *  - A ready cache is not refetched unless `force` (an admin toggle fires
 *    WORKSPACE_MODULES_UPDATED_EVENT); an `error` cache always retries.
 *  - A failed fetch is NEVER published as real features. Doing so poisoned the
 *    cache with `{}` on a transient network blip, which silently resolves every
 *    module to its registry default — undoing an admin's switch-off — for the
 *    rest of the session.
 *  - A result is dropped if the active workspace moved on while it was in
 *    flight, so a stale workspace can't clobber the current one.
 */
async function loadWorkspaceModules(workspaceId: string, force = false): Promise<void> {
  if (inFlightWorkspaceId === workspaceId) return;
  if (!force && workspaceModulesCache?.workspaceId === workspaceId
      && workspaceModulesCache.status === 'ready') {
    return;
  }
  inFlightWorkspaceId = workspaceId;
  try {
    for (let attempt = 0; ; attempt++) {
      const { data, policies, workspaceType, error } = await getWorkspaceModules(workspaceId);
      // The user switched workspace (or signed out) mid-flight — drop the result.
      if (getCurrentWorkspaceId() !== workspaceId) return;
      if (!error) {
        publishWorkspaceModulesCache(workspaceId, data, policies, 'ready', workspaceType);
        return;
      }
      if (attempt >= FETCH_RETRY_DELAYS_MS.length) {
        console.warn('[workspaceModules] load failed, using registry defaults:', error);
        publishWorkspaceModulesCache(workspaceId, {}, {}, 'error');
        return;
      }
      await new Promise((resolve) => setTimeout(resolve, FETCH_RETRY_DELAYS_MS[attempt]));
    }
  } finally {
    inFlightWorkspaceId = null;
  }
}

/**
 * React hook: the active workspace id, re-rendering when it is resolved or
 * changed.
 *
 * `getCurrentWorkspaceId()` is a plain module read, and on a cold load
 * (a pasted URL, a hard refresh, a link opened in a new tab where the
 * per-tab sessionStorage hint is absent) it answers `null` until
 * `ensureWorkspaceContext()` resolves in a Layout effect — which runs *after*
 * route children have rendered. Without this subscription, workspace-scoped
 * hooks read `null` once and never learn otherwise.
 */
export function useActiveWorkspaceId(): string | null {
  return useSyncExternalStore(
    subscribeWorkspaceContext,
    getCurrentWorkspaceId,
    () => null,
  );
}

/**
 * React hook: returns the workspace feature flags map, automatically
 * re-fetching when the active workspace resolves/changes and when
 * WORKSPACE_MODULES_UPDATED_EVENT fires.
 *
 * Uses a workspace-scoped in-memory cache so route transitions do not
 * briefly see an empty map (which would flip preview modules off/on).
 */
export function useWorkspaceModules(): Record<string, boolean> {
  const workspaceId = useActiveWorkspaceId();
  const [features, setFeatures] = useState<Record<string, boolean>>(() =>
    readCachedWorkspaceModules(workspaceId),
  );

  useEffect(() => {
    const syncFromCache = () => setFeatures(readCachedWorkspaceModules(getCurrentWorkspaceId()));
    const refetch = () => {
      const wsId = getCurrentWorkspaceId();
      if (wsId) void loadWorkspaceModules(wsId, true);
    };

    syncFromCache();
    if (workspaceId) void loadWorkspaceModules(workspaceId);
    const unsubscribe = subscribeWorkspaceModulesCache(syncFromCache);
    window.addEventListener(WORKSPACE_MODULES_UPDATED_EVENT, refetch);
    return () => {
      unsubscribe();
      window.removeEventListener(WORKSPACE_MODULES_UPDATED_EVENT, refetch);
    };
  }, [workspaceId]);

  return features;
}

/**
 * React hook: module policies for the current workspace, from the same shared
 * cache as `useWorkspaceModules` (no separate fetch, no flash of stale policy).
 */
export function useWorkspaceModulePolicies(): Record<string, ModulePolicy> {
  const workspaceId = useActiveWorkspaceId();
  const [policies, setPolicies] = useState<Record<string, ModulePolicy>>(() =>
    readCachedWorkspaceModulePolicies(workspaceId),
  );

  useEffect(() => {
    const sync = () => setPolicies(readCachedWorkspaceModulePolicies(getCurrentWorkspaceId()));
    sync();
    const unsubscribe = subscribeWorkspaceModulesCache(sync);
    window.addEventListener(WORKSPACE_MODULES_UPDATED_EVENT, sync);
    return () => {
      unsubscribe();
      window.removeEventListener(WORKSPACE_MODULES_UPDATED_EVENT, sync);
    };
  }, [workspaceId]);

  return policies;
}

/**
 * React hook: the active workspace's type (personal, team or organizational),
 * from the same shared cache as `useWorkspaceModules` — one fetch for both.
 * Null until the workspace has been read.
 */
export function useWorkspaceType(): WorkspaceType | null {
  // Mounting the modules hook is what loads (and refreshes) the shared cache.
  useWorkspaceModules();
  const workspaceId = useActiveWorkspaceId();
  return useSyncExternalStore(
    subscribeWorkspaceModulesCache,
    () => (workspaceId && workspaceModulesCache?.workspaceId === workspaceId
      ? workspaceModulesCache.workspaceType
      : null),
    () => null,
  );
}

/**
 * React hook for the canonical dual gate (non-admin resolution).
 * Prefer `useModuleGate` (moduleGate.ts), which is also admin- and
 * flag-event-reactive; this remains for existing call sites.
 */
export function useModuleFeatureAccessible(
  moduleKey: string,
  flagKey: FeatureFlagValue,
): boolean {
  const features = useWorkspaceModules();
  return isModuleFeatureAccessible(features, moduleKey, flagKey);
}

/**
 * True once workspace modules have been resolved for the active workspace —
 * i.e. the cache holds a real answer for it, whether the fetch succeeded or
 * ultimately failed.
 *
 * Consumers MUST wait on this before acting on a negative gate result. An
 * unresolved cache reads as `{}`, and `isModuleEnabled` then falls through to
 * the registry's `defaultEnabled` (on), so a module an admin switched off
 * looks on for the first render of a cold load.
 *
 * This hook subscribes to the workspace id itself, so it flips once the
 * workspace resolves after mount rather than latching on the mount-time read.
 */
export function useWorkspaceModulesReady(): boolean {
  const workspaceId = useActiveWorkspaceId();
  const [ready, setReady] = useState(() => isWorkspaceModulesCacheReady(workspaceId));

  useEffect(() => {
    const sync = () => setReady(isWorkspaceModulesCacheReady(getCurrentWorkspaceId()));
    sync();
    if (workspaceId) void loadWorkspaceModules(workspaceId);
    const unsubscribe = subscribeWorkspaceModulesCache(sync);
    const onUpdated = () => sync();
    window.addEventListener(WORKSPACE_MODULES_UPDATED_EVENT, onUpdated);
    return () => {
      unsubscribe();
      window.removeEventListener(WORKSPACE_MODULES_UPDATED_EVENT, onUpdated);
    };
  }, [workspaceId]);

  return ready;
}
